import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import { AdminCommonSteelVersion, InsertCommonSteelVersionBody } from "../../../../../http/types/commonSteelVersions";
import { useSteelVersionsApi } from "../../../../../http/useSteelVersions";
import { useVertigripSteelVersionsApi } from "../../../../../http/vertigrip/useSteelVersions";
import { PaginationQuery } from "../../../../../types";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import MessageBox from "../../../../ui/MessageBox";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import DeleteSteelVersionDialog from "../../../globalSettings/steelVersions/DeleteSteelVersionDialog";
import SteelVersionDetails, { UpsertSteelVersionForm } from "../../../globalSettings/steelVersions/SteelVersionDetails";
import styles from "./SteelVersions.module.css";

type Props = {
  active?: boolean;
};

const SteelVersions: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "steelVersions" });
  const aborter = useRef(new AbortController());
  const vertigripSteelVersionsApi = useVertigripSteelVersionsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "steelVersions");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await vertigripSteelVersionsApi.updateAdminSteelVersionVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (steelVersionId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await vertigripSteelVersionsApi.updateAdminSteelVersionOrder(steelVersionId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const steelVersionsApi = useSteelVersionsApi();
  const api = useApi();

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AdminCommonSteelVersion>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminCommonSteelVersion>("vertigrip.steel_version");
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = vertigripSteelVersionsApi.useAdminSteelVersions(query, aborter.current.signal);

  const onSubmit = async (formData: UpsertSteelVersionForm, newImage: File | undefined) => {
    const body: InsertCommonSteelVersionBody = {
      name: formData.name ?? "",
      nameIt: formData.nameIt ?? null,
      nameDe: formData.nameDe ?? null,
      nameFr: formData.nameFr ?? null,
      nameEs: formData.nameEs ?? null,
      description: formData.description!,
      descriptionIt: formData.descriptionIt ?? null,
      descriptionDe: formData.descriptionDe ?? null,
      descriptionFr: formData.descriptionFr ?? null,
      descriptionEs: formData.descriptionEs ?? null,
      order: formData.order ?? 0,
      visibility: formData.visibility,
      image: newImage?.name ?? undefined,
    };

    setIsFormSubmitting(true);

    try {
      const response = await vertigripSteelVersionsApi.insertAdminSteelVersion(body);
      if (newImage) {
        const signedUrl = await steelVersionsApi.generateUploadImageUrl(response.id, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsFormSubmitting(false);
          return;
        }
      }
      navigate(`/admin/systems/${systemId}/vertigrip_steel_versions/${response.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsFormSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.steelVersions.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.steelVersions.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminCommonSteelVersion>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/vertigrip_steel_versions/${item.id}`}
              >
                {item.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(`/admin/systems/${systemId}/vertigrip_steel_versions/${item.id}`)}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(`/admin/systems/${systemId}/vertigrip_steel_versions/${item.id}`)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {showNewItemDialog && (
        <ModalDialog
          onClose={() => setShowNewItemDialog(false)}
          title={i18n.translation.steelVersions.createNew}
          fitContent
        >
          <FlexContent direction="row">
            <SteelVersionDetails onSubmit={onSubmit} isSubmitting={isFormSubmitting} isGlobalSettings={false} />
          </FlexContent>
        </ModalDialog>
      )}

      <DeleteSteelVersionDialog
        isOpen={!!itemToDelete}
        itemIdToDelete={itemToDelete?.id ?? ""}
        onClose={() => setItemToDelete(undefined)}
      >
        <div className={styles.container}>
          <MessageBox type="error" text={i18n.translation.steelVersions.warnings.deleteWarning} />
          {i18n.translation.steelVersions.questions.delete}
        </div>
      </DeleteSteelVersionDialog>
    </FlexContent>
  );
};

export default SteelVersions;
